import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addressesPathTo } from '../utils/Api';
import { UserContext } from '../contexts/UserContext';
import SnackbarContext from '../contexts/SnackbarContext';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { layoutButtonPillRadius, layoutCardRadius, layoutShopSheetRadius } from '../theme/colors';

function formatAddressLine(address) {
  const parts = [
    address?.logradouro,
    address?.numero ? `, ${address.numero}` : '',
    address?.bairro ? ` - ${address.bairro}` : '',
  ].filter(Boolean);
  return parts.join('');
}

function formatAddressCity(address) {
  const parts = [
    address?.localidade,
    address?.uf ? `/${address.uf}` : '',
    address?.cep ? ` - ${address.cep}` : '',
  ].filter(Boolean);
  return parts.join('');
}

function getSafeReturnPath(state) {
  const path = state?.returnTo ?? state?.from;
  if (typeof path !== 'string') return null;
  if (!path.startsWith('/') || path.startsWith('//')) return null;
  return path;
}

const AddressesManagement = () => {
  const { currentUser, setCurrentUser } = useContext(UserContext);
  const { openSnackbar } = useContext(SnackbarContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [addresses, setAddresses] = useState([]);

  useEffect(() => {
    document.title = 'Endereços - TudoPets.app';
  }, []);

  const defaultAddressId = useMemo(() => {
    const fromUser = currentUser?.default_address?.id;
    if (fromUser) return fromUser;
    const fromList = addresses.find((a) => a.is_default)?.id;
    return fromList || null;
  }, [addresses, currentUser?.default_address?.id]);

  const refresh = async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const result = await addressesPathTo.list({ user_id: currentUser.id });
      setAddresses(result.data || []);
    } catch (e) {
      openSnackbar('Não foi possível carregar seus endereços', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const persistDefaultAddressOnUser = (address) => {
    const stored = JSON.parse(localStorage.getItem('current_user'));
    if (!stored) return;
    const updated = { ...stored, default_address: address };
    localStorage.setItem('current_user', JSON.stringify(updated));
    setCurrentUser(updated);
  };

  const handleSelectDefault = async (address) => {
    if (!address?.id) return;
    if (!currentUser?.id) return;
    if (address.id === defaultAddressId) return;

    try {
      await addressesPathTo.update(address.id, { user_id: currentUser.id, is_default: true });
      openSnackbar('Endereço principal selecionado!', 'success');
      persistDefaultAddressOnUser({ ...address, is_default: true });
      await refresh();
    } catch (e) {
      openSnackbar('Não foi possível selecionar esse endereço', 'error');
    }
  };

  const handleBack = () => {
    const returnTo = getSafeReturnPath(location.state);
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    navigate(-1);
  };

  const handleDelete = async (address) => {
    if (!address?.id) return;
    try {
      await addressesPathTo.delete(address.id);
      openSnackbar('Endereço removido', 'success');
      await refresh();
    } catch (e) {
      openSnackbar('Não foi possível remover esse endereço', 'error');
    }
  };

  if (!currentUser) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '24px', gap: 3, flexShrink: 0, bgcolor: 'surface.card', borderRadius: layoutShopSheetRadius }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, alignSelf: 'stretch', justifyContent: 'space-between' }}>
        <Typography variant="pageTitle" sx={{ m: 0, color: '#263238' }}>
          Endereços
        </Typography>
        <Button
          variant="contained"
          color="error"
          onClick={() => navigate('/addresses/new', { state: location.state })}
          startIcon={<AddIcon />}
          sx={{ borderRadius: layoutButtonPillRadius, fontFamily: 'Nunito', fontSize: '14px', fontWeight: 700, px: 2 }}
        >
          Adicionar
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignSelf: 'stretch' }}>
        {loading ? (
          <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', color: '#546E7A' }}>Carregando...</Typography>
        ) : null}

        {!loading && addresses.length === 0 ? (
          <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', color: '#546E7A' }}>
            Você ainda não cadastrou um endereço.
          </Typography>
        ) : null}

        {!loading && addresses.map((address) => {
          const isSelected = address.id === defaultAddressId || address.is_default;
          return (
            <Card
              key={address.id}
              sx={{
                borderRadius: layoutCardRadius,
                border: '1px solid',
                borderColor: isSelected ? '#E97837' : 'border.subtle',
                bgcolor: isSelected ? 'surface.selectedHighlight' : 'surface.card',
                boxShadow: 'none',
              }}
            >
              <CardActionArea onClick={() => handleSelectDefault(address)} sx={{ borderRadius: layoutCardRadius }}>
                <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', padding: '24px', '&:last-child': { paddingBottom: '24px' } }}>
                  <Box sx={{ pt: '2px' }}>
                    {isSelected ? (
                      <CheckCircleIcon sx={{ color: '#E97837' }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ color: '#90A4AE' }} />
                    )}
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', fontWeight: 700, lineHeight: '24px', color: '#263238' }}>
                        {formatAddressLine(address)}
                      </Typography>
                      {isSelected ? (
                        <Chip
                          label="Principal"
                          size="small"
                          sx={{ bgcolor: '#E97837', color: 'white', fontFamily: 'Nunito', fontWeight: 700 }}
                        />
                      ) : null}
                    </Box>
                    <Typography sx={{ fontFamily: 'Nunito', fontSize: '14px', fontWeight: 400, lineHeight: '21px', color: '#546E7A' }}>
                      {formatAddressCity(address)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      aria-label="Editar"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate(`/addresses/${address.id}/edit`, { state: location.state });
                      }}
                      sx={{ bgcolor: 'surface.input' }}
                    >
                      <EditIcon sx={{ color: '#546E7A' }} />
                    </IconButton>
                    <IconButton
                      aria-label="Excluir"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete(address);
                      }}
                      sx={{ bgcolor: 'surface.input' }}
                    >
                      <DeleteIcon sx={{ color: '#546E7A' }} />
                    </IconButton>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>

      <Button
        color="error"
        variant="contained"
        onClick={handleBack}
        sx={{
          display: 'flex',
          width: '100%',
          p: '16px 0',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          borderRadius: layoutButtonPillRadius,
          fontFamily: 'Nunito',
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '24px',
          bgcolor: 'secondary.dark',
        }}
      >
        Voltar
      </Button>
    </Box>
  );
};

export default AddressesManagement;
